<template>
  <div class="chatbot-wrapper">
    <TacticsStyles v-if="displayTacticContextFilters" />
    <div class="chatbot">
      <div v-if="messages.length" ref="content" class="scrollable-content">
        <div ref="list" class="message-list">
          <Node v-for="(conv, index) in messages" :key="index">
            <ErrorMessage v-if="isErrorMessage(conv.messageType)" :message="conv.message" />
            <Message v-else-if="!isContextMessage(conv.messageType)" :message="conv" />
          </Node>
        </div>
        <div :style="{ height: `${spacerHeight}px` }" />
      </div>

      <!-- Scroll to bottom button -->
      <transition name="fade">
        <BbBtn
          v-if="showScrollButton"
          class="scroll-to-bottom-btn"
          type="icon"
          icon="chevron-down"
          icon-size="medium"
          @click="scrollToBottom"
        />
      </transition>
    </div>
    <MessageInput @onSubmit="onSubmitValue" />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { messageType, queryType } from '~/constants/conversationModels';

import Message from '~/components/conversation/chatMessage/Message';
import Node from '~/components/conversation/chatMessage/Node';
import MessageInput from '~/components/conversation/MessageInput';
import useConversations from '~/composables/useConversations';
import ErrorMessage from '~/components/conversation/chatMessage/ErrorMessage.vue';
import useAuthoring from '~/composables/useAuthoring';
import TacticsStyles from '~/components/conversation/authoring/tacticsContexts/TacticContextAppliedStylesInConversation.vue';
import { useTopAnchoredScroll } from '~/composables/useTopAnchoredScroll';

export default {
  name: 'Conversation',
  components: {
    ErrorMessage,
    MessageInput,
    Message,
    Node,
    TacticsStyles,
  },

  setup() {
    const route = useRoute();
    const currentRouteConversationId = ref(route.params.conversationId);

    const { currentConversation, publishToConversation } = useConversations();
    const { tacticContextFilters } = useAuthoring();

    const messages = computed(() => currentConversation.value.messages);

    const { content, list, spacerHeight, showScrollButton, scrollToBottom, prepareForNewMessage, resetScrollState } =
      useTopAnchoredScroll(messages);

    const conversationId = computed(() => currentConversation.value.conversationId);
    const nrOfContexts = computed(() => Object.values(tacticContextFilters.value).length);

    const isErrorMessage = (type) => type === messageType.error;
    const isContextMessage = (type) => type === messageType.context;

    const onSubmitValue = (value) => {
      prepareForNewMessage();
      publishToConversation(conversationId.value, queryType.authoringQuery, value);
    };

    watch(
      () => route.params.conversationId,
      async () => {
        // we'll need the value of conversationId from route in order to pass it to unsubscribe;
        // onBeforeUnmount can't see the correct value from route.params.conversationId if you navigate to /authoring
        currentRouteConversationId.value = route.params.conversationId;
        resetScrollState();
      },
      { immediate: true, deep: true },
    );

    return {
      content,
      list,
      spacerHeight,
      isContextMessage,
      isErrorMessage,
      messages,
      onSubmitValue,
      showScrollButton,
      scrollToBottom,
      displayTacticContextFilters: nrOfContexts,
    };
  },
};
</script>

<style lang="scss" scoped>
.chatbot-wrapper {
  --primary-color: #{$bb-primary-1};
  --primary-dimmed-color: #{$bb-primary-1-dimmed};

  @include bb-base-font-family;
  @include bb-regular-text-styles('md');

  @include media-breakpoint-up('sm') {
  }

  // height: 100%;

  // color: $bb-neutral-1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: antialiased;
  font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%; /* Prevent adjustments of font size after orientation changes in iOS */

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  max-width: 524px;
  // margin: auto;
  padding: $bb-spacing-5;
  background-color: white;
  flex: 1;
  border-radius: $bb-spacing-4;
  box-shadow: 0px 5px 8px 0px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-height: 0;

  .chatbot {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    margin-bottom: $bb-spacing-2;
    position: relative;
  }

  .scrollable-content {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding-right: $bb-spacing-4;

    //display: flex;
    //align-items: flex-end;
    //flex-wrap: wrap;
    //height: 100%;

    //&__inner {
    //width: 100%;
    //}
  }

  .message-list {
    display: flow-root;
  }

  .scroll-to-bottom-btn {
    position: absolute;
    bottom: $bb-spacing-4;
    left: 50%;
    transform: translateX(-50%);
    width: 40px !important;
    height: 40px !important;
    border-radius: 50%;
    background-color: $bb-neutral-7;
    border: 1px solid $bb-neutral-5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 10;

    &:hover {
      background-color: $bb-neutral-6 !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: translateX(-50%) scale(0.95);
    }

    :deep(svg),
    :deep(i) {
      color: $bb-neutral-2;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  :deep(.inputs-wrapper.inputs-disabled) {
    min-height: 120px;
  }
}
</style>
